/**
 * Maintien de l'écran allumé pendant le suivi.
 *
 * Équivalent du `FLAG_KEEP_SCREEN_ON` posé par `HudController` tant que le
 * suivi est actif : sans lui, l'écran s'éteint au bout de quelques dizaines de
 * secondes en roulant, et le téléphone se reverrouille.
 *
 * Deux particularités de l'API web imposent la mécanique ci-dessous :
 *
 *  - le verrou est **relâché par le navigateur** dès que la page passe en
 *    arrière-plan — changement d'onglet, écran éteint à la main, appel reçu.
 *    Il faut donc le reprendre au retour, sans quoi le suivi reprendrait sans
 *    protection ;
 *  - il n'existe qu'en **contexte sécurisé**. En HTTP sur une adresse de
 *    réseau local, la demande échoue : c'est une raison de plus de tester en
 *    HTTPS.
 *
 * Toute erreur est absorbée : un écran qui s'éteint est un désagrément, pas
 * une raison d'interrompre la navigation.
 */

/** Sous-ensemble utilisé de l'API, pour ne pas dépendre de sa présence. */
interface WakeLockSentinelLike {
  release(): Promise<void>
  readonly released: boolean
}

interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>
}

function wakeLockApi(): WakeLockLike | null {
  const candidate = (navigator as { wakeLock?: WakeLockLike }).wakeLock
  return candidate ?? null
}

export class ScreenWakeLock {
  #sentinel: WakeLockSentinelLike | null = null

  /** Le verrou est-il souhaité ? Distinct de sa détention effective. */
  #wanted = false

  #listening = false

  /** L'API est-elle exploitable ici ? */
  get supported(): boolean {
    return wakeLockApi() !== null
  }

  /** Le verrou est-il effectivement détenu ? */
  get held(): boolean {
    return this.#sentinel !== null && !this.#sentinel.released
  }

  /** Demande le maintien de l'écran, et le reprend après chaque interruption. */
  async acquire(): Promise<void> {
    this.#wanted = true
    this.#listen()
    await this.#request()
  }

  /** Rend le verrou et cesse de le reprendre. */
  async release(): Promise<void> {
    this.#wanted = false

    const sentinel = this.#sentinel
    this.#sentinel = null

    if (sentinel === null) return

    try {
      await sentinel.release()
    } catch {
      // Déjà relâché par le navigateur : rien à faire.
    }
  }

  async #request(): Promise<void> {
    if (!this.#wanted || this.held) return

    // Une demande faite alors que la page est masquée échoue à coup sûr ;
    // l'écoute du retour au premier plan s'en chargera.
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return
    }

    const api = wakeLockApi()
    if (api === null) return

    try {
      this.#sentinel = await api.request('screen')
    } catch {
      // Contexte non sécurisé, économie d'énergie active, permission refusée :
      // le suivi continue sans maintien de l'écran.
      this.#sentinel = null
    }
  }

  #listen(): void {
    if (this.#listening || typeof document === 'undefined') return

    this.#listening = true
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.#request()
    })
  }
}
